import { Component, Input, OnChanges } from '@angular/core';
import { NgIf } from '@angular/common';
import Highcharts from 'highcharts';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { NoWidgetDataComponent } from '../../my-brand-screen/no-widget-data/no-widget-data.component';
import { MyCompetitionAudienceGenderBrand } from '../types';
import { GENDER_CHART_COLORS } from '../../my-brand-screen/types';

@Component({
  selector: 'app-audience-gender-chart',
  standalone: true,
  imports: [NgIf, LoaderComponent, NoWidgetDataComponent],
  templateUrl: './audience-gender-chart.component.html',
  styleUrl: './audience-gender-chart.component.scss'
})
export class AudienceGenderChartComponent implements OnChanges {
  @Input() loading = false;
  @Input() data: MyCompetitionAudienceGenderBrand[] = [];

  public readonly containerId = 'audience-gender-chart-container';
  private chartInstance: Highcharts.Chart | undefined;

  private readonly MEN_COLOR = GENDER_CHART_COLORS.men;
  private readonly WOMEN_COLOR = GENDER_CHART_COLORS.women;
  private readonly NO_DATA_COLOR = GENDER_CHART_COLORS.noData;

  private readonly textStyle = {
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '400',
    color: '#4E5258'
  };

  ngOnChanges(): void {
    this.setChartInstance();
    this.updateChartOptions();
  }

  public get hasData(): boolean {
    return this.data?.length > 0 && this.data.some(b => b.menPercentage !== null || b.womenPercentage !== null);
  }

  private setChartInstance(): void {
    if (!this.hasData && this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }
    if (this.hasData && !this.chartInstance) {
      this.chartInstance = Highcharts.chart(this.containerId, this.buildChartOptions());
    }
  }

  private updateChartOptions(): void {
    if (!this.chartInstance || !this.hasData) return;
    this.chartInstance.update(this.buildChartOptions(), true, true);
  }

  private buildChartOptions(): Highcharts.Options {
    const categories = this.data.map(b => this.truncateName(b.brandName));
    const data = this.data;

    const menData = this.data.map(b => ({
      y: b.menPercentage !== null ? -b.menPercentage : -50,
      color: b.menPercentage !== null ? this.MEN_COLOR : this.NO_DATA_COLOR,
    }));

    const womenData = this.data.map(b => ({
      y: b.womenPercentage !== null ? b.womenPercentage : 50,
      color: b.womenPercentage !== null ? this.WOMEN_COLOR : this.NO_DATA_COLOR,
    }));

    return {
      chart: {
        type: 'bar',
        height: 296,
        animation: false,
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: {
        useHTML: true,
        formatter: function (this: any) {
          const idx = this.point.index;
          const brand = data[idx];
          if (brand.menPercentage === null) {
            return `<span style="font-family:Poppins,sans-serif;font-size:12px">No gender data available</span>`;
          }
          return `<span style="font-family:Poppins,sans-serif;font-size:12px">
            <b>${brand.brandName}</b><br/>
            Men ${brand.menPercentage}%<br/>
            Women ${brand.womenPercentage}%
          </span>`;
        },
        style: { fontFamily: 'Poppins', fontSize: '12px', color: '#4e5258' }
      },
      xAxis: {
        categories,
        reversed: false,
        lineWidth: 0,
        tickLength: 0,
        labels: { style: this.textStyle, align: 'right' }
      },
      yAxis: {
        min: -100,
        max: 100,
        tickInterval: 20,
        title: { text: undefined },
        gridLineWidth: 0.5,
        labels: {
          formatter: function () { return Math.abs(this.value as number) + '%'; },
          style: this.textStyle
        },
      },
      plotOptions: {
        bar: {
          stacking: 'normal',
          borderWidth: 0,
          borderRadius: 4,
          groupPadding: 0,
          pointPadding: 0.1,
        },
        series: {
          animation: false,
          states: {
            inactive: { opacity: 1 }
          },
          point: {
            events: {
              mouseOver: function (this: any) {
                const hoveredIndex = this.index;
                this.series.chart.series.forEach((s: any) => {
                  s.points.forEach((p: any) => {
                    p.graphic?.attr({ opacity: p.index === hoveredIndex ? 1 : 0.3 });
                  });
                });
              },
              mouseOut: function (this: any) {
                this.series.chart.series.forEach((s: any) => {
                  s.points.forEach((p: any) => {
                    p.graphic?.attr({ opacity: 1 });
                  });
                });
              }
            }
          }
        }
      },
      series: [
        {
          type: 'bar',
          name: 'Men',
          data: menData,
          colorByPoint: true,
        },
        {
          type: 'bar',
          name: 'Women',
          data: womenData,
          colorByPoint: true,
        }
      ]
    };
  }

  private truncateName(name: string): string {
    return name.length > 14 ? name.slice(0, 12) + '..' : name;
  }
}
